import { useEffect, useRef, useState, useCallback } from 'react';
import { SubtitleResponse } from '../types/auth';
import { api } from '../services/api';

const POLL_INTERVAL_MS = 5000;

export interface UseSubtitlesReturn {
  subtitles: SubtitleResponse[];
  selectedSubtitle: SubtitleResponse | null;
  selectSubtitle: (language: string | null) => void;
  requestSubtitle: (language: string) => Promise<void>;
  error: string | null;
}

export function useSubtitles(
  contentId: number,
  contentType: 'live' | 'video',
  _authToken: string | null,
): UseSubtitlesReturn {
  const [subtitles, setSubtitles] = useState<SubtitleResponse[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showError = useCallback((msg: string) => {
    setError(msg);
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    errorTimerRef.current = setTimeout(() => setError(null), 5000);
  }, []);

  // Load subtitles on mount
  useEffect(() => {
    const load = contentType === 'live'
      ? () => api.getLiveSubtitles(contentId)
      : () => api.getVideoSubtitles(contentId);
    load().then(setSubtitles).catch(() => {});
  }, [contentId, contentType]);

  // Poll for pending/in-progress subtitles
  useEffect(() => {
    const hasPending = subtitles.some(s => s.status === 'Pending' || s.status === 'InProgress');
    if (!hasPending) {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      return;
    }
    if (pollTimerRef.current) return; // already polling

    const load = contentType === 'live'
      ? () => api.getLiveSubtitles(contentId)
      : () => api.getVideoSubtitles(contentId);

    pollTimerRef.current = setInterval(() => {
      load().then(setSubtitles).catch(() => {});
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [subtitles, contentId, contentType]);

  const selectedSubtitle = subtitles.find(
    s => s.language === selectedLanguage && s.isAvailable,
  ) ?? null;

  const selectSubtitle = useCallback((language: string | null) => {
    setSelectedLanguage(language);
  }, []);

  const requestSubtitle = useCallback(async (language: string) => {
    try {
      const resp = contentType === 'live'
        ? await api.requestLiveSubtitle(contentId, language)
        : await api.requestVideoSubtitle(contentId, language);
      setSubtitles(prev => {
        const exists = prev.find(s => s.language === language);
        if (exists) {
          return prev.map(s => s.language === language ? resp : s);
        }
        return [...prev, resp];
      });
      setSelectedLanguage(language);
    } catch (err: any) {
      showError(err.message || 'Failed to request subtitles');
    }
  }, [contentId, contentType, showError]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    };
  }, []);

  return {
    subtitles,
    selectedSubtitle,
    selectSubtitle,
    requestSubtitle,
    error,
  };
}
